import { claude, skill } from '../api.js';
import { curPage } from '../state.js';
import { stripHtmlToText } from './helpers.js';

// ── EDIT COORDINATOR ─────────────────────────────────────────────
// Unified routing + execution for ALL edit contexts.
// Pure functions — no global state access. All dependencies injected via opts.
//
// routeInstruction() — lightweight routing (Haiku)
// applyInstruction() — full orchestration (route -> refine -> edit)
//
// Dependencies: claude() from api.js, skill() from api.js

// Pure orchestration logic — minimal DOM dependency. Near-direct migration to composable.

// ── ROUTING ──────────────────────────────────────────────────────

const ROUTE_SCHEMA = {
  type: 'object',
  additionalProperties: false,
  properties: {
    targets: {
      type: 'array',
      items: { type: 'string' },
      description: 'Affected targets. Empty array if ambiguous — use clarification instead.',
    },
    steps: {
      type: 'array',
      items: {
        type: 'object',
        additionalProperties: false,
        properties: {
          targets: { type: 'array', items: { type: 'string' } },
          instruction: {
            type: 'string',
            description: 'The specific sub-instruction for this step',
          },
        },
        required: ['targets', 'instruction'],
      },
      description:
        'For compound instructions only — ordered sub-steps. Empty array for simple instructions.',
    },
    clarification: {
      type: 'string',
      description:
        'If ambiguous or no clear target, write a short question. Empty if targets are clear.',
    },
    reasoning: { type: 'string' },
  },
  required: ['targets', 'steps', 'clarification', 'reasoning'],
};

const ROUTE_PROMPT = `<role>
You route website edit instructions to the correct targets.
</role>

<task>
Determine which targets the instruction affects. Return them as an array.
If ambiguous, return empty targets + a clarification question.

For compound instructions with multiple distinct changes (e.g. "make the hero dark and add a pricing section"), decompose into ordered steps in the "steps" array. Each step has its own targets and a focused sub-instruction.
For simple instructions, leave steps empty — just use targets.
</task>

<target_types>
- "tokens" — CSS value changes: a specific color, font, spacing, or radius.
- "vision" — design direction changes: overall aesthetic, feel, atmosphere.
- Section ID — content or layout changes in that section.
- "add:{type}" — add a new section (e.g. "add:testimonials").
- "add:{type}:after:{sectionId}" — add after a specific section.
- "remove:{sectionId}" — remove a section.
- "rebuild:{sectionId}" — regenerate a section from scratch.
- "suggest:{sectionId}" — analyze a section and suggest improvements. Use when user asks to "suggest improvements", "what can I improve", "analyze this section", "any ideas for the hero".
</target_types>

<section_identification>
Match sections using any signal:
- Type name: "the pricing section" → pricing
- Title content: "the section about testimonials" → match by title
- Position: "the third section" → section #3
- Visual: "the dark section" → match bgStyle. If multiple, ask.
- Neighbor: "the section after hero" → next in list
- Semantic: "where the client quotes are" → testimonials
- Targeting tag: if [targeting: id] present, use it as a hint — but if the instruction explicitly names a DIFFERENT section (e.g. "translate the about section" while targeting FAQ), prefer the section named in the instruction.

If multiple could match and the instruction doesn't name one, ask. If "it"/"that section" without context, use the targeting tag or ask.
</section_identification>

<rules>
- "vision" for aesthetic/feel ("more dramatic", "like Apple's website").
- "tokens" for specific CSS values ("make CTA red", "use Poppins").
- Section ID for content/layout within a section.
- "add:{type}" when user asks to add ("add testimonials", "I need pricing").
- "remove:{id}" for deletion ("remove the FAQ", "delete testimonials").
- "rebuild:{id}" for redo ("redo the hero", "start features over").
- Prefer narrow scope — one section cheaper than many.
- NEVER guess. Empty targets + clarification > wrong targets.

Aliases: "banner"/"main section" = hero, "nav"/"menu" = header, "bottom" = footer, "form" = contact.

Decomposition:
- ONLY decompose when the instruction targets DIFFERENT systems (e.g. tokens AND a section, or two different sections).
- NEVER decompose when all changes target the SAME section — pass the full instruction as one edit. The section editor handles multi-part changes within a section.
- "Change accent to blue and update the hero CTA text" → steps: [{targets:["tokens"], instruction:"change accent color to blue"}, {targets:["hero"], instruction:"update the CTA button text"}] (different systems: tokens + section)
- "Make the hero full width with text on the right and a dark overlay" → targets:["hero"], steps:[] (all changes in one section — no decomposition)
- "Add a gradient to the hero and make the text white" → targets:["hero"], steps:[] (same section)
- "Make it bigger" → no steps, just targets.
</rules>`;

/**
 * Extract a text content summary from section HTML (first ~120 chars).
 */
const sectionSummary = html => {
  const text = stripHtmlToText(html, 120);
  return text.length >= 120 ? `${text}\u2026` : text;
};

/**
 * Compress older chat messages into a summary line.
 * Returns [summary?, ...recent6]
 */
const buildChatContext = chatLog => {
  const recent = chatLog.slice(-8);
  const older = chatLog.slice(0, -8);
  if (!older.length) {
    return recent;
  }

  // Compress older messages into a summary
  const summary = older
    .filter(m => m.role === 'user' || (m.role === 'ai' && m.applied))
    .map(m => (m.role === 'user' ? `User: ${m.text}` : `AI: ${m.applied} (${m.scope})`))
    .join(' → ');

  if (!summary) {
    return recent;
  }
  return [{ role: '_summary', text: summary }, ...recent];
};

/**
 * Route an instruction to affected targets.
 * @param {string} instruction
 * @param {Array<{id: string, type: string, title: string, content?: string}>} sections
 * @param {Array} [chatContext] — chat messages (may include compressed summary)
 * @returns {Promise<{targets: string[], steps: Array, clarification: string}>}
 */
const routeInstruction = async (instruction, sections, chatContext = []) => {
  const list = sections
    .map((s, i) => {
      let line = `${i + 1}. ${s.id} (type: ${s.type}, title: "${s.title}", bg: ${s.bgStyle || 'light'}, built: ${s.status === 'done' ? 'yes' : 'no'})`;
      if (s.content) {
        line += `\n   Content: "${s.content}"`;
      }
      return line;
    })
    .join('\n');

  // Build multi-turn messages from chat context (may include compressed summary of older messages)
  const messages = [];
  for (const m of chatContext) {
    if (m.role === '_summary') {
      messages.push({ role: 'user', content: `[Earlier conversation summary: ${m.text}]` });
      messages.push({
        role: 'assistant',
        content: 'Understood, I have context from the earlier conversation.',
      });
    } else if (m.role === 'user') {
      messages.push({ role: 'user', content: m.text });
    } else if (m.role === 'ai' && m.applied) {
      messages.push({ role: 'assistant', content: `Applied: ${m.applied} (scope: ${m.scope})` });
    }
  }
  // Final user message: sections context + current instruction
  messages.push({
    role: 'user',
    content: `<sections>\n${list}\n</sections>\n\n<instruction>${instruction}</instruction>`,
  });

  try {
    const result = await claude({
      sys: ROUTE_PROMPT,
      user: instruction,
      messages,
      model: 'claude-haiku-4-5',
      tokens: 350,
      schema: ROUTE_SCHEMA,
    });
    const parsed = typeof result === 'string' ? JSON.parse(result) : result;
    return {
      targets: parsed.targets || [],
      steps: parsed.steps || [],
      clarification: parsed.clarification || '',
    };
  } catch (e) {
    console.warn('Edit routing failed:', e);
    // Re-throw — network/API errors should surface as errors, not fake clarifications.
    // sendChatMessage catches this and shows a retry-able error in chat.
    throw e;
  }
};

// ── EXECUTION ────────────────────────────────────────────────────

/**
 * Full-page edit fallback — used when no specific sections or tokens are targeted.
 * Returns {html, summary}.
 */
const fullPageEdit = async (instruction, html, editHistory, onChunk) => {
  const sk = skill('edit');
  const histBlock = editHistory.length
    ? `<edit_history>\n${editHistory.map((h, i) => `${i + 1}. "${h}" — applied`).join('\n')}\n${
        editHistory.length + 1
      }. "${instruction}" — current request\n</edit_history>\nPreserve ALL changes from previous edits. Apply only the current request.\n\n`
    : '';

  const rawHtml = await claude({
    sys: sk.prompt || '',
    user: `${histBlock}<current_page>\n${html}\n</current_page>\n\n<instruction>${instruction}</instruction>\n\nBegin your response with a one-line HTML comment summarizing what you changed:\n<!-- APPLIED: description of changes -->\nThen output the full page HTML.`,
    tokens: sk.tokens || 8000,
    model: sk.model,
    temp: sk.temp,
    effort: sk.effort,
    onChunk,
  });

  const applyMatch = rawHtml.match(/<!--\s*APPLIED:\s*(.+?)\s*-->/);
  const summary = applyMatch?.[1] || '';
  const cleanHtml = rawHtml.replace(/<!--\s*APPLIED:.*?-->\s*/, '');
  return { html: cleanHtml, summary };
};

/**
 * Apply an edit instruction — routes, refines tokens, edits sections.
 * Works for design preview, build/sections, and preview/edit.
 * Zero global state access — everything injected via opts.
 *
 * @param {string} instruction — user's edit text
 * @param {Object} opts
 * @param {Array<{id, type, title}>} opts.sections — current page sections (for routing)
 * @param {string[]}  opts.validIds — section IDs present in assembled HTML
 * @param {Function}  opts.getHtml — returns current assembled HTML
 * @param {string[]}  [opts.editHistory] — previous global edit instructions
 * @param {Array}     [opts.recentChat] — last 6 chat messages
 * @param {Function}  [opts.onChunk] — streaming chunk callback
 * @param {Function}  [opts.refineTokens] — async (instruction) => summary string
 * @param {Function}  [opts.refineVision] — async (instruction) => summary string
 * @param {Function}  [opts.editSection] — async (instruction, sectionId, html, onChunk) => {html, summary}
 * @param {Function}  [opts.addSection] — (typeId, afterId) => newSect
 * @param {Function}  [opts.removeSection] — (id) => void
 * @param {Function}  [opts.rebuildSection] — async (id, instruction) => html
 * @returns {Promise<{html: string|null, targets: string[], scope: string, summary: string, clarification?: string}>}
 */
const applyInstruction = async (instruction, opts) => {
  const {
    sections,
    validIds,
    getHtml,
    editHistory = [],
    onChunk,
    recentChat = [],
    refineTokens,
    refineVision,
    editSection,
    addSection,
    removeSection,
    rebuildSection,
  } = opts;

  // Strip [targeting: ...] prefix — it's routing metadata, not part of the edit instruction
  const cleanInstruction = instruction.replace(/^\[targeting:\s*[^\]]+\]\s*/i, '');

  // 1. Route
  const chatContext = buildChatContext(recentChat);
  const route = await routeInstruction(instruction, sections, chatContext);

  // 2. Clarification — return early
  if (!route.targets.length && route.clarification) {
    return {
      html: null,
      targets: [],
      scope: 'clarify',
      summary: '',
      clarification: route.clarification,
    };
  }

  // 2b. Decomposed steps — execute each step sequentially
  if (route.steps?.length > 1) {
    let html = null;
    let summary = '';
    const allTargets = [];
    for (const step of route.steps) {
      // Carry forward parent targets so sub-steps don't lose context
      let stepInstr = step.instruction;
      const stepTargets = step.targets?.length ? step.targets : route.targets;
      if (stepTargets.length) {
        stepInstr = `[targeting: ${stepTargets.join(',')}] ${step.instruction}`;
      }
      const stepResult = await applyInstruction(stepInstr, {
        ...opts,
        recentChat: [],
        getHtml: () => html || getHtml(),
      });
      if (stepResult.scope === 'clarify') {
        return stepResult;
      }
      if (stepResult.html) {
        html = stepResult.html;
      }
      if (stepResult.summary) {
        summary += (summary ? '. ' : '') + stepResult.summary;
      }
      allTargets.push(...stepResult.targets);
    }
    const scope = allTargets.filter(t => !t.includes(':')).join(',') || 'global';
    return { html, targets: allTargets, scope, summary };
  }

  const targets = route.targets;

  // 3. Classify
  const suggestTargets = targets.filter(t => t.startsWith('suggest:'));
  if (suggestTargets.length) {
    const ids = suggestTargets.map(t => t.split(':')[1]);
    return { html: null, targets, scope: 'suggest', summary: '', suggestIds: ids };
  }

  const addTargets = targets.filter(t => t.startsWith('add:'));
  const removeTargets = targets.filter(t => t.startsWith('remove:'));
  const rebuildTargets = targets.filter(t => t.startsWith('rebuild:'));
  const sectionTargets = targets.filter(
    t =>
      !(t.startsWith('add:') || t.startsWith('remove:') || t.startsWith('rebuild:')) &&
      t !== 'tokens' &&
      t !== 'vision' &&
      validIds.includes(t),
  );
  const touchesTokens = targets.includes('tokens') || targets.includes('vision');

  // 4. Management actions first
  for (const t of removeTargets) {
    if (removeSection) {
      removeSection(t.split(':')[1]);
    }
  }
  const addedSections = [];
  for (const t of addTargets) {
    const parts = t.split(':');
    if (addSection) {
      const newSect = addSection(parts[1], parts[3] || null, cleanInstruction);
      if (newSect) {
        addedSections.push(newSect);
      }
    }
  }
  for (const sect of addedSections) {
    if (rebuildSection) {
      await rebuildSection(sect.id);
    }
  }

  // 5. Refine vision or tokens
  let summary = '';
  if (targets.includes('vision') && refineVision) {
    const s = await refineVision(cleanInstruction);
    if (s) {
      summary = s;
    }
  } else if (targets.includes('tokens') && refineTokens) {
    const s = await refineTokens(cleanInstruction);
    if (s) {
      summary = s;
    }
  }

  // 6. Rebuild targets
  for (const t of rebuildTargets) {
    const id = t.split(':')[1];
    if (rebuildSection) {
      await rebuildSection(id, cleanInstruction);
      summary += (summary ? '. ' : '') + `Rebuilt ${id}`;
    }
  }

  // 7. Section-scoped edits (unbuilt sections → rebuild instead)
  let html = null;
  if (sectionTargets.length && editSection) {
    html = getHtml();
    for (const id of sectionTargets) {
      const sect = curPage().sects.find(s => s.id === id);
      if (sect && sect.status !== 'done') {
        if (rebuildSection) {
          await rebuildSection(id, cleanInstruction);
        }
        summary += (summary ? '. ' : '') + `Rebuilt ${id}`;
        continue;
      }
      const editResult = await editSection(cleanInstruction, id, html, onChunk);
      html = editResult.html;
      summary += (summary ? '. ' : '') + (editResult.summary || `Edited ${id}`);
    }
  } else if (
    !(touchesTokens || addTargets.length || removeTargets.length || rebuildTargets.length)
  ) {
    const editResult = await fullPageEdit(cleanInstruction, getHtml(), editHistory, onChunk);
    html = editResult.html;
    summary = editResult.summary || 'Applied global edit';
  }

  // 8. Management action summaries
  if (addTargets.length) {
    const addedTypes = addTargets.map(t => t.split(':')[1]).join(', ');
    summary +=
      (summary ? '. ' : '') +
      (addedSections.length ? `Added and built ${addedTypes}` : `Added ${addedTypes}`);
  }
  if (removeTargets.length) {
    summary +=
      (summary ? '. ' : '') + `Removed ${removeTargets.map(t => t.split(':')[1]).join(', ')}`;
  }

  // 9. Scope for chat log
  const allTargets = [...sectionTargets, ...rebuildTargets.map(t => t.split(':')[1])];
  const scope = allTargets.length
    ? allTargets.join(',')
    : addTargets.length
      ? 'add'
      : removeTargets.length
        ? 'remove'
        : touchesTokens
          ? targets.includes('vision')
            ? 'vision'
            : 'tokens'
          : 'global';

  return { html, targets, scope, summary };
};

// ── MODULE EXPORTS ──────
export {
  buildChatContext,
  fullPageEdit,
  ROUTE_PROMPT,
  ROUTE_SCHEMA,
  sectionSummary,
  applyInstruction,
  routeInstruction,
};
